import { Injectable, inject } from '@angular/core';
import { Observable, Subject, map, distinctUntilChanged } from 'rxjs';
import { GradingStateService } from './grading-state.service';

/**
 * Badge state for the grading indicator
 */
export interface GradingBadgeState {
  /** Whether grading has been performed (latestGradingRunId is set) */
  isActive: boolean;
}

/**
 * Service for deriving grading badge state from the grading session.
 * Provides reactive observable for the badge UI component.
 */
@Injectable()
export class GradingBadgeService {
  private readonly stateService = inject(GradingStateService);

  /**
   * Subject to trigger scrolling to the grading results preview
   */
  private readonly scrollToGradingSubject = new Subject<void>();

  /**
   * Observable that emits when scrolling to grading results should occur
   */
  readonly scrollToGrading$ = this.scrollToGradingSubject.asObservable();

  /**
   * Observable of badge state derived from session
   */
  readonly badgeState$: Observable<GradingBadgeState> = this.stateService.session$.pipe(
    map((session) => ({
      isActive: session?.latestGradingRunId != null,
    })),
    distinctUntilChanged((prev, curr) => prev.isActive === curr.isActive)
  );

  /**
   * Observable for whether grading is active
   */
  readonly isActive$: Observable<boolean> = this.badgeState$.pipe(
    map((state) => state.isActive)
  );

  /**
   * Trigger scroll to the grading results preview
   */
  scrollToGrading(): void {
    this.scrollToGradingSubject.next();
  }
}
